import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCustomer } from "../../lib/customer.jsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FARMER_TYPES = ["Pequeño agricultor", "Productor mediano", "Gran empresa agrícola", "Agrónomo", "Cooperativa", "Otro"];
const MAIN_CROPS = ["Cítricos", "Frutas tropicales", "Hortalizas", "Ornamentales", "Viñedo", "Cultivos extensivos", "Otro"];
const HECTARES = ["< 1 ha", "1 - 5 ha", "5 - 20 ha", "20 - 100 ha", "100 - 500 ha", "+ 500 ha"];
const HOW = ["Google", "Instagram", "Facebook", "WhatsApp", "Recomendación", "Flora IA", "Feria / Evento", "Otro"];

function CouponCard({ customer }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(customer.coupon_code);
      setCopied(true);
      toast.success("Cupón copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("No se pudo copiar"); }
  };
  const expiresAt = customer.coupon_expires_at ? new Date(customer.coupon_expires_at) : null;
  return (
    <div className="bg-[#0e2618] border border-[#b45f3e]/30 rounded-2xl p-8 text-center" data-testid="customer-coupon">
      <div className="eu-data-label text-[#9ead9b] mb-3">TU CUPÓN DE BIENVENIDA · 10% DESCUENTO</div>
      <div className="font-mono-eu text-3xl lg:text-4xl text-[#b45f3e] font-bold tracking-widest my-4 select-all">
        {customer.coupon_code}
      </div>
      <button onClick={copy} className="text-xs bg-[#f7f7f4] text-[#133322] rounded-full px-5 py-2 hover:bg-[#b45f3e] hover:text-[#f7f7f4] transition" data-testid="copy-coupon">
        <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"} mr-2`}></i>
        {copied ? "Copiado" : "Copiar código"}
      </button>
      {expiresAt && (
        <div className="text-xs text-[#9ead9b] mt-4">Válido hasta {expiresAt.toLocaleDateString("es-ES")} (90 días)</div>
      )}
    </div>
  );
}

function SampleForm() {
  const { customer, requestSample } = useCustomer();
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ name: "", address: "", city: "", postal_code: "", country: customer?.country || "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/countries`).then(r => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  if (customer?.sample_requested) {
    return (
      <div className="bg-[#f7f7f4]/[0.05] border border-[#f7f7f4]/15 rounded-2xl p-8 text-center" data-testid="sample-already">
        <i className="fa-solid fa-circle-check text-3xl text-[#9ead9b]"></i>
        <h4 className="font-display text-2xl text-[#f7f7f4] mt-3">Solicitud de muestra recibida</h4>
        <p className="text-[#f7f7f4]/70 text-sm mt-2">Te contactaremos pronto para coordinar el envío y el pago del transporte.</p>
      </div>
    );
  }

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestSample(form);
      toast.success("Solicitud enviada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-[#f7f7f4]/[0.06] rounded-xl px-4 py-3 text-sm outline-none border border-[#f7f7f4]/15 focus:border-[#b45f3e] text-[#f7f7f4] placeholder:text-[#f7f7f4]/40";
  const labelCls = "eu-data-label block mb-2 text-[#9ead9b]";

  return (
    <form onSubmit={submit} className="bg-[#f7f7f4]/[0.04] border border-[#f7f7f4]/10 rounded-2xl p-8" data-testid="sample-form">
      <div className="eu-data-label text-[#9ead9b] mb-2">MUESTRA GRATUITA</div>
      <h4 className="font-display text-2xl text-[#f7f7f4] leading-tight">1 Kg Engorde Complet NPK 6-5-43</h4>
      <p className="text-xs text-[#9ead9b] mt-3 leading-relaxed">
        EUZERONE regala 1 Kg de Engorde Complet. El coste del envío corre a cargo del solicitante para cualquier destino. Te contactaremos para coordinar el pago del transporte.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelCls}>Nombre destinatario *</label>
          <input required value={form.name} onChange={upd("name")} className={inputCls} data-testid="sample-name" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Dirección *</label>
          <input required value={form.address} onChange={upd("address")} className={inputCls} data-testid="sample-address" />
        </div>
        <div>
          <label className={labelCls}>Ciudad *</label>
          <input required value={form.city} onChange={upd("city")} className={inputCls} data-testid="sample-city" />
        </div>
        <div>
          <label className={labelCls}>Código postal *</label>
          <input required value={form.postal_code} onChange={upd("postal_code")} className={inputCls} data-testid="sample-postal" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>País *</label>
          <select required value={form.country} onChange={upd("country")} className={inputCls} data-testid="sample-country">
            <option value="">— Selecciona —</option>
            {countries.map(c => <option key={c} value={c} className="text-[#133322]">{c}</option>)}
          </select>
        </div>
      </div>

      <button type="submit" disabled={busy} className="mt-6 w-full bg-[#b45f3e] hover:bg-[#a04f31] text-[#f7f7f4] rounded-full py-3 font-medium transition disabled:opacity-50" data-testid="sample-submit">
        <i className="fa-solid fa-truck text-xs mr-2"></i>
        {busy ? "Enviando…" : "Solicitar muestra gratuita"}
      </button>
    </form>
  );
}

function CustomerPanel() {
  const { customer, logout } = useCustomer();
  return (
    <div className="space-y-8" data-testid="customer-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="eu-data-label text-[#9ead9b]">CUENTA · ACTIVA</div>
          <h3 className="font-display text-3xl lg:text-4xl text-[#f7f7f4] mt-2">¡Bienvenido, {customer.first_name}!</h3>
          <p className="text-[#9ead9b] text-sm mt-1">{customer.email}</p>
        </div>
        <button onClick={logout} className="text-xs text-[#9ead9b] hover:text-[#f7f7f4] border border-[#f7f7f4]/20 rounded-full px-4 py-2" data-testid="customer-logout">
          <i className="fa-solid fa-right-from-bracket mr-2"></i> Cerrar sesión
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <CouponCard customer={customer} />
        <SampleForm />
      </div>
    </div>
  );
}

function RegisterForm({ onSwitchToLogin }) {
  const { register } = useCustomer();
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", password_confirm: "",
    phone: "", country: "", farmer_type: "", main_crops: [], hectares: "",
    how_did_you_know: "", accept_terms: false, accept_marketing: false,
  });
  const [busy, setBusy] = useState(false);
  const passwordMismatch = form.password_confirm.length > 0 && form.password !== form.password_confirm;
  const passwordShort = form.password.length > 0 && form.password.length < 8;

  useEffect(() => {
    axios.get(`${API}/countries`).then(r => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleCrop = (crop) => {
    const arr = form.main_crops.includes(crop) ? form.main_crops.filter(c => c !== crop) : [...form.main_crops, crop];
    setForm({ ...form, main_crops: arr });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (passwordMismatch) { toast.error("Las contraseñas no coinciden"); return; }
    if (passwordShort) { toast.error("Mínimo 8 caracteres"); return; }
    if (!form.accept_terms) { toast.error("Debes aceptar los términos"); return; }
    setBusy(true);
    try {
      const { password_confirm: _, ...payload } = form;
      await register(payload);
      toast.success("¡Registro completado!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrarte");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-[#f7f7f4]/[0.06] rounded-xl px-4 py-3 text-sm outline-none border border-[#f7f7f4]/15 focus:border-[#b45f3e] text-[#f7f7f4] placeholder:text-[#f7f7f4]/40";
  const labelCls = "eu-data-label block mb-2 text-[#9ead9b]";

  return (
    <form onSubmit={submit} className="bg-[#f7f7f4]/[0.04] border border-[#f7f7f4]/10 rounded-2xl p-8 lg:p-10" data-testid="register-form">
      <div className="flex items-center justify-between mb-6">
        <div className="eu-data-label text-[#9ead9b]">REGISTRO GRATUITO</div>
        <button type="button" onClick={onSwitchToLogin} className="text-xs text-[#9ead9b] hover:text-[#f7f7f4]" data-testid="switch-to-login">
          ¿Ya tienes cuenta? <span className="underline">Iniciar sesión</span>
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Nombre *</label>
          <input required value={form.first_name} onChange={upd("first_name")} className={inputCls} data-testid="reg-first-name" />
        </div>
        <div>
          <label className={labelCls}>Apellidos *</label>
          <input required value={form.last_name} onChange={upd("last_name")} className={inputCls} data-testid="reg-last-name" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Email *</label>
          <input required type="email" value={form.email} onChange={upd("email")} className={inputCls} data-testid="reg-email" />
        </div>
        <div>
          <label className={labelCls}>Contraseña *</label>
          <input required type="password" minLength={8} value={form.password} onChange={upd("password")} className={inputCls} data-testid="reg-password" />
          {passwordShort && <div className="text-xs text-[#b45f3e] mt-1">Mínimo 8 caracteres</div>}
        </div>
        <div>
          <label className={labelCls}>Confirmar contraseña *</label>
          <input required type="password" minLength={8} value={form.password_confirm} onChange={upd("password_confirm")} className={inputCls} data-testid="reg-password-confirm" />
          {passwordMismatch && <div className="text-xs text-[#b45f3e] mt-1">No coinciden</div>}
        </div>
        <div>
          <label className={labelCls}>Teléfono</label>
          <input value={form.phone} onChange={upd("phone")} className={inputCls} placeholder="+34 600 000 000" />
        </div>
        <div>
          <label className={labelCls}>País *</label>
          <select required value={form.country} onChange={upd("country")} className={inputCls} data-testid="reg-country">
            <option value="">— Selecciona —</option>
            {countries.map(c => <option key={c} value={c} className="text-[#133322]">{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Tipo de agricultor</label>
          <select value={form.farmer_type} onChange={upd("farmer_type")} className={inputCls}>
            <option value="">—</option>
            {FARMER_TYPES.map(t => <option key={t} value={t} className="text-[#133322]">{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Hectáreas gestionadas</label>
          <select value={form.hectares} onChange={upd("hectares")} className={inputCls}>
            <option value="">—</option>
            {HECTARES.map(h => <option key={h} value={h} className="text-[#133322]">{h}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Cultivos principales</label>
          <div className="flex flex-wrap gap-2">
            {MAIN_CROPS.map(crop => {
              const active = form.main_crops.includes(crop);
              return (
                <button type="button" key={crop} onClick={() => toggleCrop(crop)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? "bg-[#b45f3e] text-[#f7f7f4] border-[#b45f3e]" : "bg-transparent text-[#9ead9b] border-[#f7f7f4]/20 hover:border-[#b45f3e]"}`}>
                  {active && <i className="fa-solid fa-check text-[10px] mr-1.5"></i>}
                  {crop}
                </button>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>¿Cómo nos conociste?</label>
          <select value={form.how_did_you_know} onChange={upd("how_did_you_know")} className={inputCls}>
            <option value="">—</option>
            {HOW.map(h => <option key={h} value={h} className="text-[#133322]">{h}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <label className="flex items-start gap-3 text-sm text-[#f7f7f4]/85 cursor-pointer">
          <input required type="checkbox" checked={form.accept_terms} onChange={upd("accept_terms")} className="mt-1 accent-[#b45f3e]" data-testid="reg-terms" />
          <span>Acepto los <a href="#" className="underline text-[#9ead9b]">términos y la política de privacidad</a> *</span>
        </label>
        <label className="flex items-start gap-3 text-sm text-[#f7f7f4]/85 cursor-pointer">
          <input type="checkbox" checked={form.accept_marketing} onChange={upd("accept_marketing")} className="mt-1 accent-[#b45f3e]" />
          <span>Acepto recibir comunicaciones comerciales (cupones, ofertas, novedades técnicas).</span>
        </label>
      </div>

      <button type="submit" disabled={busy || passwordMismatch || passwordShort} className="mt-8 w-full bg-[#b45f3e] hover:bg-[#a04f31] disabled:opacity-50 text-[#f7f7f4] rounded-full py-3.5 font-medium transition" data-testid="reg-submit">
        <i className="fa-solid fa-seedling text-xs mr-2"></i>
        {busy ? "Creando cuenta…" : "Registrarme y obtener cupón"}
      </button>
    </form>
  );
}

function LoginForm({ onSwitchToRegister }) {
  const { login } = useCustomer();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); toast.success("Sesión iniciada"); }
    catch (err) { toast.error(err.response?.data?.detail || "Credenciales inválidas"); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full bg-[#f7f7f4]/[0.06] rounded-xl px-4 py-3 text-sm outline-none border border-[#f7f7f4]/15 focus:border-[#b45f3e] text-[#f7f7f4]";
  const labelCls = "eu-data-label block mb-2 text-[#9ead9b]";

  return (
    <form onSubmit={submit} className="bg-[#f7f7f4]/[0.04] border border-[#f7f7f4]/10 rounded-2xl p-8 lg:p-10 max-w-md mx-auto" data-testid="login-form">
      <div className="flex items-center justify-between mb-6">
        <div className="eu-data-label text-[#9ead9b]">INICIAR SESIÓN</div>
        <button type="button" onClick={onSwitchToRegister} className="text-xs text-[#9ead9b] hover:text-[#f7f7f4]" data-testid="switch-to-register">
          ¿Sin cuenta? <span className="underline">Regístrate</span>
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className={labelCls}>Email</label>
          <input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className={inputCls} data-testid="login-email" />
        </div>
        <div>
          <label className={labelCls}>Contraseña</label>
          <input required type="password" value={password} onChange={(e)=>setPassword(e.target.value)} className={inputCls} data-testid="login-password" />
        </div>
      </div>
      <button type="submit" disabled={busy} className="mt-6 w-full bg-[#b45f3e] hover:bg-[#a04f31] text-[#f7f7f4] rounded-full py-3 font-medium transition disabled:opacity-50" data-testid="login-submit">
        {busy ? "Entrando…" : "Iniciar sesión"}
      </button>
    </form>
  );
}

export function RegistroContent({ variant = "section" }) {
  const { customer, loading } = useCustomer();
  const [mode, setMode] = useState("register");
  const isModal = variant === "modal";

  const benefits = [
    { icon: "fa-ticket", title: "Cupón 10% descuento", text: "Cupón de bienvenida automático aplicable a tu primer pedido." },
    { icon: "fa-box", title: "Muestra 1Kg gratis", text: "1 Kg de Engorde Complet NPK 6-5-43. Solo pagas envío." },
    { icon: "fa-file-lines", title: "Guías técnicas", text: "Material agronómico exclusivo por cultivo y etapa fenológica." },
    { icon: "fa-rocket", title: "Acceso anticipado", text: "Sé el primero en conocer nuevos productos antes del lanzamiento." },
  ];

  return (
    <div className={isModal ? "p-6 sm:p-10" : "relative max-w-6xl mx-auto px-6 lg:px-10"}>
      {!customer && (
        <div className={`${isModal ? "" : "text-center max-w-3xl mx-auto mb-14"}`}>
          <div className={`eu-data-label text-[#9ead9b] mb-4 ${isModal ? "" : ""}`}>
            REGISTRO {isModal ? "" : "· 07"}
          </div>
          <h2 className={`font-display ${isModal ? "text-3xl lg:text-4xl text-left" : "text-4xl lg:text-6xl text-center"} text-[#f7f7f4] leading-[1.05] tracking-tight`}>
            Únete a la <em className="text-[#b45f3e] not-italic">comunidad</em> EUZERONE
          </h2>
          <p className={`mt-5 ${isModal ? "text-sm text-left" : "text-base lg:text-lg text-center"} text-[#f7f7f4]/80 font-light`}>
            Recibe ofertas exclusivas, promociones, contenido técnico y tu muestra gratuita.
          </p>
        </div>
      )}

      {!customer && (
        <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-${isModal ? "3 my-6" : "4 mb-14"}`}>
          {benefits.map((b, i) => (
            <article key={i} className={`bg-[#f7f7f4]/[0.04] border border-[#f7f7f4]/10 rounded-2xl ${isModal ? "p-4" : "p-6"}`} data-testid={`reg-benefit-${i}`}>
              <div className={`${isModal ? "h-8 w-8 mb-3" : "h-10 w-10 mb-4"} rounded-full bg-[#b45f3e]/20 border border-[#b45f3e]/30 flex items-center justify-center`}>
                <i className={`fa-solid ${b.icon} text-[#b45f3e] text-sm`}></i>
              </div>
              <h3 className={`font-display ${isModal ? "text-sm" : "text-lg"} text-[#f7f7f4] leading-tight`}>{b.title}</h3>
              {!isModal && <p className="mt-2 text-xs text-[#f7f7f4]/70 leading-relaxed">{b.text}</p>}
            </article>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-[#9ead9b]">Cargando…</div>
      ) : customer ? (
        <CustomerPanel />
      ) : mode === "register" ? (
        <RegisterForm onSwitchToLogin={() => setMode("login")} />
      ) : (
        <LoginForm onSwitchToRegister={() => setMode("register")} />
      )}
    </div>
  );
}

export default function Registro() {
  return (
    <section id="registro" className="py-24 lg:py-32 bg-[#0e2618] text-[#f7f7f4] relative overflow-hidden" data-testid="registro-section">
      <div className="absolute inset-0 pointer-events-none opacity-25">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#b45f3e]/30 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#9ead9b]/20 blur-3xl" />
      </div>
      <RegistroContent variant="section" />
    </section>
  );
}
